// @atlas/contracts — a `revenue-forecast` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { z } from 'zod';
import { BillingTypeSchema, toQueryArray } from '../_shared/schemas.js';
/** Számlázási gyakoriság a PRR-frekvencia-bontáshoz (a nem-havi recurring tételek). */
const REVENUE_PERIODIC_FREQUENCIES = ['quarterly', 'semi_annual', 'annual'];
/** A szűrő-sáv query-paraméterei. Minden opcionális; `status` üres → default [active] (a BFF-ben). */
export const RevenueForecastQuerySchema = z.object({
    /**
     * SZABAD-SZÖVEGES kereső — ügyfél-, fiók-, szolgáltatás- és csomag-NÉVRE illeszt (kis/nagybetű-érzéketlen,
     * részszó). A kimutatás nem lista, hanem AGGREGÁTUM: a keresés tehát nem sorokat emel ki, hanem SZŰKÍTI azt
     * a halmazt, amin a kártyák és a diagramok készülnek — ugyanúgy, ahogy a többi szűrő.
     */
    q: z.string().trim().max(200).optional(),
    /** Célhónap (YYYY-MM). Hiány → az aktuális hónap (Europe/Budapest). */
    month: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
        .optional(),
    /** Státusz-szűrő (multi). Elfogad ismételt query-paramétert VAGY egyetlen értéket. Üres → [active]. */
    status: z.preprocess(toQueryArray, z.array(z.enum(['active', 'pending', 'inactive']))).optional(),
    /** Főkategória-szűrő (MULTI). Ismételt query-param VAGY egyetlen érték. Üres → mind (a legacy `whereIn` 1:1). */
    mainCategory: z
        .preprocess(toQueryArray, z.array(z.enum(['product', 'advisory', 'other'])))
        .optional(),
    /** Alkategória-szűrő (MULTI). Ismételt query-param VAGY egyetlen érték. Üres → mind. */
    subCategory: z.preprocess(toQueryArray, z.array(z.string())).optional(),
    /**
     * DÍJTÍPUS-szűrő (egyértékű): `recurring` = ismétlődő, `one_time` = eseti; hiány → mind. A hozzárendelés
     * `billingType`-jára illeszt — komponens-gyereknél a SZÜLŐÉRE (a gyerek a szülő számlájának része). A kézi
     * (hozzárendelés nélküli) számla-tétel díjtípusa ismeretlen, ezért bekapcsolt szűrőnél kiesik.
     */
    billingType: BillingTypeSchema.optional(),
    /** Szolgáltatás-szűrő (a katalógus-service id-je; a plan→service leképezésen át). */
    serviceId: z.string().optional(),
    /** Csomag-szűrő (a hozzárendelés servicePlanId-je). */
    planId: z.string().optional(),
    clientId: z.string().optional(),
    accountId: z.string().optional(),
});
/** Egy PRR-frekvencia-bontás sor (Decimal-string összeg). */
const RevenuePrrByFrequencySchema = z.object({
    frequency: z.enum(REVENUE_PERIODIC_FREQUENCIES),
    amount: z.string(),
});
/** Egy összeg-státusz-bontás (a `*_by_status` multi-státusz badge-ek forrása; Decimal-string). */
const RevenueByStatusSchema = z.object({
    active: z.string(),
    pending: z.string(),
});
/** Egy komponens-sor a drilldown children-bontásában (komplex/csomag hozzárendelés). */
const RevenueDetailChildSchema = z.object({
    name: z.string(),
    price: z.string(),
});
/** Egy hozzárendelés-sor a szolgáltatás-drilldown-ban. */
const RevenueServiceDetailItemSchema = z.object({
    scope: z.enum(['client', 'account']),
    status: z.string(),
    clientName: z.string(),
    accountName: z.string(),
    /**
     * A hónapra eső összeg a kimutatás szabályával (Decimal-string): havi tételnél a havidíj, negyedéves / féléves /
     * éves tételnél a TELJES díj a számla hónapjában, máskor `'0.00'`.
     */
    mrr: z.string(),
    /** Áll-e ki BIZONYLAT erre a tételre a hónapban. */
    isDue: z.boolean(),
    /** A teljes periódus-díj (Decimal-string): havi tételnél a havidíj, éves tételnél az éves összeg. */
    periodPrice: z.string(),
    /** Periodikus tételnél a következő számla hónapja (`YYYY-MM`), ha a hónapban nincs számla; különben `null`. */
    nextBilledMonth: z.string().nullable(),
    frequency: z.string(),
    isOneTime: z.boolean(),
    isComplex: z.boolean(),
    children: z.array(RevenueDetailChildSchema),
});
/** Egy szolgáltatás-csoport a drilldown-ban (a byService-oszlophoz igazítva). */
const RevenueServiceDetailGroupSchema = z.object({
    serviceName: z.string(),
    totalMrr: z.string(),
    assignments: z.array(RevenueServiceDetailItemSchema),
});
/** Egy NRR-detail sor (a szem-ikon modál). */
const RevenueNrrDetailSchema = z.object({
    service: z.string(),
    planName: z.string().nullable(),
    clientName: z.string(),
    accountName: z.string(),
    status: z.string(),
    price: z.string(),
});
/** Egy PRR-detail sor (a szem-ikon modál) — NRR + scope + frekvencia. */
const RevenuePrrDetailSchema = RevenueNrrDetailSchema.extend({
    scope: z.enum(['client', 'account']),
    frequency: z.string(),
});
/** Egy függőben-tétel sor (a szem-ikon modál) — a TÉNYLEGES ár (nem havi-normalizált). */
const RevenuePendingDetailSchema = z.object({
    service: z.string(),
    planName: z.string().nullable(),
    scope: z.enum(['client', 'account']),
    clientName: z.string(),
    accountName: z.string(),
    price: z.string(),
    frequency: z.string(),
});
/** A kártyák (stats). Minden pénz Decimal-string; `mrrChangePct` szám (előjeles százalék, null ha nincs bázis). */
const RevenueForecastStatsSchema = z.object({
    /** MRR: havi frekvenciájú recurring bevétel a célhónapra — EREDMÉNYSZEMLÉLET (a szolgáltatás hónapja). */
    mrr: z.string(),
    /** Az előző havi MRR (a változás-badge bázisa). */
    mrrPrev: z.string(),
    /** Változás az előző hónaphoz (%). null, ha az előző havi MRR 0 (nincs értelmes bázis). */
    mrrChangePct: z.number().nullable(),
    /** Az MRR státusz-bontása (active/pending) — a multi-státusz badge (legacy `mrr_by_status`). */
    mrrByStatus: RevenueByStatusSchema,
    /**
     * PRR: nem-havi (negyedéves/féléves/éves) recurring bevétel a célhónapra — a TELJES periódus-díj, abban a
     * hónapban, amikor a számla kiáll (user-döntés: egy éves díj egyetlen hónapban, egyben jelenik meg).
     */
    prr: z.string(),
    /** A PRR frekvencia szerinti bontása. */
    prrByFrequency: z.array(RevenuePrrByFrequencySchema),
    /** A PRR státusz-bontása (legacy `periodic_by_status`). */
    prrByStatus: RevenueByStatusSchema,
    /** NRR: a célhónaphoz tartozó eseti (one_time) díjak (a tétel kezdő hónapja). */
    nrr: z.string(),
    /** Az NRR státusz-bontása (legacy NRR `by_status`). */
    nrrByStatus: RevenueByStatusSchema,
    /**
     * ARR: a cél-hónappal BEZÁRÓLAG gördülő 12 hónap recurring bevétele a kimutatás szabályával (havidíjak + a
     * kiszámlázott periodikus díjak; NEM MRR×12). GÖRDÜLŐ, nem naptári év: azzal a mutató január 1-jén leesett, és a hónap sorszámáról szólt.
     */
    arr: z.string(),
    /** AR: ARR + az UGYANAZON a gördülő 12 hónapon INDULÓ összes eseti (one_time) tétel teljes ára (nem NRR×12). */
    ar: z.string(),
    /** A következő naptári hónap várható bevétele (mindig aktív státusz, arányosítással). */
    nextMonth: z.string(),
    /** A KIVÁLASZTOTT (szűrt) hónap bevétele — mindig aktív státusz, arányosítással (a legacy zöld kártya). */
    selectedMonth: z.string(),
    /** A szűrt, a hónapban ÉRVÉNYES recurring hozzárendelések darabszáma — aktív. */
    activeCount: z.number(),
    /** …függőben. */
    pendingCount: z.number(),
    /**
     * …INAKTÍV, LEZÁRATLAN: inaktív, a hónapban még érvényes, de NYITOTT vagy JÖVŐBELI záró dátumú — nem számlázódik
     * (a már lezárt inaktív sor a záró dátumig az aktívak közt számol). = `inactiveDetails.length`.
     */
    inactiveCount: z.number(),
    /** …összesen (a szűrt, érvényes halmaz mérete). */
    totalCount: z.number(),
    /** NRR-részletek (a szem-ikon modál tétel-listája). */
    nrrDetails: z.array(RevenueNrrDetailSchema),
    /** PRR-részletek (a szem-ikon modál tétel-listája). */
    prrDetails: z.array(RevenuePrrDetailSchema),
    /** Függőben-tételek (a szem-ikon modál) — CSAK szolgáltatás/csomag-szűrővel szűkítve (legacy 1:1). */
    pendingDetails: z.array(RevenuePendingDetailSchema),
    /**
     * Az INAKTÍV, LEZÁRATLAN tételek (nyitott / jövőbeli záró dátum — nem számlázódnak): a státusz-sáv „inaktív,
     * lezáratlan" eleme ezt listázza. Ugyanabból a halmazból, mint az `inactiveCount`.
     */
    inactiveDetails: z.array(RevenuePendingDetailSchema),
});
/** A chartok. Minden pénz Decimal-string. */
const RevenueForecastChartsSchema = z.object({
    /** MRR-trend: az utolsó 12 hónap (a célhónappal bezárólag), régitől újig; a havi tételek havidíja. */
    mrrTrend: z.array(z.object({ month: z.string(), amount: z.string() })),
    /**
     * Szolgáltatásonként, a kimutatás szabályával (havi tétel: havidíj; periodikus: a teljes díj a számla hónapjában)
     * + a hónapban számlázott eseti teljes ára, csökkenő.
     * CSOPORTOSÍTÁS: szolgáltatás- / csomag-szűrő NÉLKÜL szolgáltatásonként EGY sor (a csomagjai összevonva); szűrve a
     * csomag-szintű bontás („csomag (szolgáltatás)"). `fixed` + `usage` = `amount`: a fix díjú és a használat-alapú
     * (sávos / alap+sávos / jutalékos) rész. `serviceId`: a felület erre szűr kattintásra.
     */
    byService: z.array(z.object({
        name: z.string(),
        serviceId: z.string().nullable(),
        amount: z.string(),
        fixed: z.string(),
        usage: z.string(),
    })),
    /** Top 10 ügyfél, a kimutatás szabályával (recurring + a hónapban számlázott eseti teljes ár), stacked. */
    byClient: z.array(z.object({
        name: z.string(),
        recurring: z.string(),
        oneTime: z.string(),
        total: z.string(),
    })),
    /** Főkategória-megoszlás, a kimutatás szabályával (recurring + a hónapban számlázott eseti teljes ára). */
    categoryDistribution: z.array(z.object({ category: z.enum(['product', 'advisory', 'other']), amount: z.string() })),
    /** Szolgáltatás-részletes drilldown — CSAK ügyfél/fiók VAGY szolgáltatás/csomag VAGY alkategória-szűrő esetén (különben üres). */
    serviceDetails: z.array(RevenueServiceDetailGroupSchema),
    /**
     * TERV-eredmény trend (12 hónap, régitől újig) — a `mrrTrend`-től eltérően a TELJES havi eredmény
     * (recurring + eseti), tehát a `actualTrend`-del ÖSSZEVETHETŐ. A kiválasztott-hónap kártya
     * (`stats.selectedMonth`) ugyanezt a mennyiséget méri.
     */
    planTrend: z.array(z.object({ month: z.string(), amount: z.string() })),
    /**
     * TÉNY trend (ugyanaz a 12 hónap): a kiszámlázott bizonylatokból. Az `amount` **`null`**, ha a hónap még
     * NYITOTT (nem futott le rá a generálás) — ott nincs tény-szám, és a 0 félrevezető lenne.
     */
    actualTrend: z.array(z.object({ month: z.string(), amount: z.string().nullable() })),
});
/**
 * Egy tény-oldali bontás sora. A `name` **`null`**, ha a sor nem sorolható be (kézi számla vagy már törölt
 * hozzárendelés) — a CÍMKÉT a felület adja i18n-kulcsból, a BFF felhasználói szöveget nem hardcode-ol.
 */
const RevenueActualBreakdownSchema = z.object({
    name: z.string().nullable(),
    amount: z.string(),
});
/**
 * A TÉNY-oldal: mennyit SZÁMLÁZTUNK KI a kiválasztott hónapra (`docs/modules/admin/finance.md` §4 „A TÉNY-oldal”).
 *
 * Minden számla ÉS díjbekérő beleszámít (piszkozat/kiállított/fizetett/lejárt), KIVÉVE a semmis állapotúakat
 * (sztornó, érvénytelenített). A generált tétel a PERIÓDUSA hónapjaira terül szét; a kézi tétel a
 * teljesítés- (híján kiállítás-) hónapját terheli, és a „Besorolatlan" vödörbe esik.
 */
const RevenueActualSchema = z.object({
    /**
     * LEZÁRT-e a kiválasztott hónap, azaz van-e rá értelmes tény-szám. HÁROM feltétel, MIND kell:
     * (1) a hónap VÉGET ÉRT — a futó és a jövőbeli hónap sosem lezárt; (2) van rá ADAT: bizonylat-tétel VAGY
     * kihagyás-napló sor; (3) LEFUTOTT a rá vonatkozó számlázási kör — kelt bizonylat a RÁKÖVETKEZŐ hónapban
     * (az utólag fizetett sorok számlája a periódus utáni hónapban áll ki). `false` → a felület nem mutat
     * tény-számot (a 0 azt sugallná, hogy elmaradt a bevétel, pedig csak a kör nem zárult le).
     */
    closed: z.boolean(),
    /** A hónap tény-összege HUF-ban (Decimal-string). `closed=false` → `'0.00'`, és nem jelenik meg. */
    total: z.string(),
    /** Eltérés a tervtől (tény − terv), Decimal-string; előjeles. */
    variance: z.string(),
    /** Az eltérés százalékban; `null`, ha a terv 0 (nincs értelmes bázis). */
    variancePct: z.number().nullable(),
    /** Hány NYITOTT kivétel maradt a hónapra a kihagyás-naplóban — a „miért" sor száma. */
    skippedOpen: z.number().int(),
    /**
     * A hónap árfolyam-minősége: `exact` (kézi árfolyam vagy csak HUF) · `estimated` (MNB havi átlag) ·
     * `fallback` (nem volt feloldható árfolyam → 1:1, a deviza-forgalom ALULBECSÜLT).
     */
    rateSource: z.enum(['exact', 'estimated', 'fallback']),
    /** Igaz, ha a bizonylat-olvasás elérte a lapozó-plafont (a szám csonka lehet). */
    truncated: z.boolean(),
    byService: z.array(RevenueActualBreakdownSchema),
    byClient: z.array(RevenueActualBreakdownSchema),
    /** A terv-oldal három kategóriája + a kézi számla `unclassified` vödre. */
    categoryDistribution: z.array(z.object({
        category: z.enum(['product', 'advisory', 'other', 'unclassified']),
        amount: z.string(),
    })),
});
/** A `usagePricing.missingItems` lista felső korlátja (a teljes darabszám a `missing`). */
export const USAGE_MISSING_ITEMS_MAX = 100;
export const RevenueForecastResponseSchema = z.object({
    /** A ténylegesen feldolgozott célhónap (YYYY-MM) — a UI ezt tükrözi vissza a datepickerbe. */
    month: z.string(),
    currency: z.literal('HUF'),
    /** Igaz, ha BÁRMELY deviza-tétel (EUR/USD) árfolyama nem volt betölthető (MNB néma 1:1 fallback) → a UI
     *  diszkrét figyelmeztetést mutat (a deviza-forgalom becsült/1:1 lehet). Csak-HUF halmaznál mindig false. */
    usedFallbackRate: z.boolean(),
    /**
     * AZ ADAT-HORIZONT (`YYYY-MM`): mely hónaptól TELJES az egyes forrás az Atlasban — a felület ebből mondja ki a
     * vezetőségnek, hogy visszamenőleg a kalkuláció adathiány miatt nem lehet pontos (ami nincs, azt nem lehet
     * becsülni). `actualsFrom` előtt nincs tény-szám; `usageFrom` előtt a sávos/jutalékos tétel csak bázisdíjjal
     * becsülhető; `oneTimeFrom` előtt az eseti díjak hiányosan vannak rögzítve.
     */
    dataHorizon: z.object({
        actualsFrom: z.string(),
        usageFrom: z.string(),
        oneTimeFrom: z.string(),
    }),
    /**
     * A HASZNÁLAT-ALAPÚ (sávos / alap+sávos / jutalékos) tételek állapota a célhónapra.
     *
     * `priced` — a hónap MÉRT mennyiségéből számolt összeggel szerepelnek (ugyanaz az árazó-motor, mint a
     * számlán). `missing` — a hónapra még NINCS használati adat, ezért a BÁZISDÍJ (alapdíj/passzív díj)
     * szerepel: a szám tehát alsó becslés. A felület ezt kimondja, mert magából az összegből nem látszik.
     *
     * `monthOpen` — a célhónap a FUTÓ vagy egy jövőbeli hónap: a használati adat a hónap LEZÁRÁSA után érkezik,
     * tehát itt a `missing` a normál állapot (semleges tájékoztatás), nem hiány. Lezárt hónapban a `missing`
     * valódi hiány → a felület figyelmeztet, és a `missingItems` listával megmondja, MELYIK tételek ezek
     * (user-lelet: a futó hónap „273 tétel nem kapott adatot" mondata hibát sugallt, a lezárt hónap 20 valódi
     * hiánya pedig név nélkül nem volt kereshető). A `missingItems` legfeljebb {@link USAGE_MISSING_ITEMS_MAX} sor;
     * a teljes darabszám a `missing`. A mért NULLA `priced` (a díj 0 / a passzív díj), nem hiány.
     */
    usagePricing: z.object({
        priced: z.number().int(),
        missing: z.number().int(),
        monthOpen: z.boolean(),
        missingItems: z.array(z.object({
            clientName: z.string().nullable(),
            accountName: z.string().nullable(),
            itemName: z.string().nullable(),
        })),
    }),
    stats: RevenueForecastStatsSchema,
    charts: RevenueForecastChartsSchema,
    /** A TÉNY-oldal (kiszámlázott bizonylatok) — a terv melletti második szám és a bontásai. */
    actual: RevenueActualSchema,
    /**
     * KINTLÉVŐSÉG a mai napra: a kiállított, ki nem fizetett számlák és díjbekérők BRUTTÓ összege devizánként,
     * benne a lejártak (státusz `overdue`, vagy a határidő már elmúlt). Cég-szintű pillanatkép — a lap szűrői nem
     * hatnak rá. `null`, ha a bizonylat-olvasás nem sikerült (a lap ilyenkor nem mutat számot).
     */
    receivables: z
        .object({
        asOf: z.string(),
        totals: z.array(z.object({
            currency: z.string(),
            outstanding: z.string(),
            count: z.number(),
            overdue: z.string(),
            overdueCount: z.number(),
        })),
    })
        .nullable(),
});
export const RevenueRowSchema = z.object({
    id: z.string(),
    /** `plan` = a hozzárendelésből SZÁMOLT (jelen/jövő), `actual` = a kiállított bizonylatból (múlt). */
    source: z.enum(['plan', 'actual']),
    clientName: z.string().nullable(),
    accountName: z.string().nullable(),
    /**
     * A sor EFFEKTÍV számlázási profiljának neve — az, ami a kiállított bizonylaton áll.
     *
     * MIÉRT A SZERVER OLDJA FEL: a választás szabálya PÉNZ-releváns (fiók saját számlázása → a fiók profilja,
     * különben az ügyfél elsődlegese), és ugyanaz, amit a havi generátor alkalmaz — két helyen vezetve a lista
     * más nevet mutatna, mint amit a számla visz. Nincs profil vagy nem olvasható → `null`.
     */
    billingName: z.string().nullable(),
    serviceName: z.string().nullable(),
    planName: z.string().nullable(),
    mainCategory: z.string().nullable(),
    subCategory: z.string().nullable(),
    /** `client` | `account` — a hozzárendelés hatóköre. */
    scope: z.string().nullable(),
    /** `active` | `pending` | `inactive`; BESOROLATLAN tény-soron (kézi számla) `null`. */
    status: z.string().nullable(),
    /** `recurring` | `one_time`. */
    billingType: z.string().nullable(),
    billingFrequency: z.string().nullable(),
    /** A hónap nettó összege (havi tétel: havidíj; periodikus: a teljes díj a számla hónapjában). */
    amount: z.string(),
    /**
     * A hónapra jutó ÁFA és BRUTTÓ — ugyanabban a szemléletben, mint az `amount`.
     *
     * TÉNY-soron a BIZONYLAT saját áfája/bruttója, a nettóval AZONOS osztással hónapokra bontva (a hónapok
     * összege így bájtra a bizonylat-tétel értéke marad). TERV-soron a terv-pillanatkép áfakulcsából, a
     * bizonylat-számoló kanonikus kerekítésével — tehát nem becslés: pontosan az, amit a havi generátor is ki
     * fog állítani ugyanabból a mezőből (`invoice-generation.service.ts` → `snap.vatRate`).
     */
    vatAmount: z.string(),
    grossAmount: z.string(),
    /** Tény-soron a bizonylat (link a számla-részletre); terv-soron `null`. */
    invoiceId: z.string().nullable(),
    invoiceNumber: z.string().nullable(),
    assignmentId: z.string().nullable(),
    clientId: z.string().nullable(),
    accountId: z.string().nullable(),
    serviceId: z.string().nullable(),
    servicePlanId: z.string().nullable(),
});
/** A bevétel-lista query-je: a kimutatás szűrői + lapozás. */
export const RevenueRowsQuerySchema = RevenueForecastQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(200).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
export const RevenueRowsResponseSchema = z.object({
    /** A ténylegesen feldolgozott hónap (`YYYY-MM`). */
    month: z.string(),
    /**
     * A lista FORRÁSA. `actual` = a hónap lezárult, a sorok a kiállított bizonylatokból jönnek; `plan` = a
     * sorok a hozzárendelésekből számolódnak (jelen/jövő hónap, vagy egy még le nem generált múlt hónap).
     */
    source: z.enum(['plan', 'actual']),
    /** Lezárult-e a hónap — három feltétel: véget ért · van rá adat · lefutott a rá vonatkozó számlázási kör. */
    closed: z.boolean(),
    /** Az ADAT-HORIZONT (l. a kimutatás `dataHorizon`-ját) — a lista ebből mondja ki, ha a hónapra nincs tény. */
    dataHorizon: z.object({
        actualsFrom: z.string(),
        usageFrom: z.string(),
        oneTimeFrom: z.string(),
    }),
    rows: z.array(RevenueRowSchema),
    /** A SZŰRT halmaz mérete (nem a lapé). */
    total: z.number(),
    /** A szűrt halmaz ÖSSZEGE a hónapra (Decimal-string, HUF) — a lista meta-sora. */
    totalAmount: z.string(),
    nextOffset: z.number().nullable(),
    /** Igaz, ha valamelyik deviza árfolyama nem volt feloldható (a deviza-tételek 1:1-gyel szerepelnek). */
    usedFallbackRate: z.boolean(),
    /** Igaz, ha a bizonylat-olvasás elérte a lapozó-plafont (a tény-lista csonka lehet). */
    truncated: z.boolean(),
});
//# sourceMappingURL=schemas.js.map